/* Common Imports*/

import { styled } from "@mui/system";
import React from "react";

/* Component Imports */

import {
	Alert,
	Divider,
	IconButton,
	Link,
	Skeleton,
	Snackbar,
	SvgIcon,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { ObjectId } from "bson";
import { useRouter } from "next/router";
import MailIcon from "../../../../../public/images/icons/social/mail-new.svg";
import CopyIcon from "../../../../../public/images/icons/social/new_copy.svg";
import WhatsAppIcon from "../../../../../public/images/icons/social/whatsapp-icon.svg";
import { AddingBeegruPointsForSharePropertyThunk, CreateNewSharesThunk } from "../../../../../redux-magic/store";

/* Styled Components */

const Container = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	padding: "1rem 1rem 1rem 1rem",
	background: theme.palette.background.paper,
	margin: "0rem 0rem 0rem 0rem",
	borderRadius: "16px",
}));

const Heading = styled(Typography)(({ theme }) => ({
	margin: "0rem 0rem 0.5rem 0rem",
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const SocialButtonsConatiner = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-around",
	alignItems: "flex-start",
	width: "100%",
	margin: "0rem 0rem 0rem 0rem",
}));

const SocialPaper = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	height: "4.5rem !important",
	width: "4.5rem !important",
	gap: "0.5rem",
	margin: "0.5rem 0rem 0.5rem 0rem",
	borderRadius: "8px",
	cursor: "pointer",
	// "@media (pointer:fine)": {
	// 	"&:hover": {
	// 		scale: "1.1",
	// 	},
	// },
}));

const ImgTag = styled("img")(({ theme }) => ({
	height: "1.875rem",
	width: "1.875rem",
}));

const CopyRow = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "center",
	margin: "0.5rem 0rem 0rem 0rem",
	width: "100%",
	[theme.breakpoints.down("sm")]: {
		width: "100% !important",
		padding: "0rem",
	},
}));

const CopyInput = styled(TextField)(({ theme }) => ({
	width: "100%",
	margin: 0,
	height: "3.5rem",
	[theme.breakpoints.down("sm")]: {
		height: "3.5rem !important",
	},
}));

const Label = styled(Typography)(({ theme }) => ({
	fontWeight: "400",
	fontSize: "0.75rem",
	lineHeight: "0.75rem",
	textAlign: "center",
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	[theme.breakpoints.up("sm")]: {
		marginTop: "2.5rem",
	},
}));

const ShareProperty = ({
	url,
	loading,
	showOnPhone,
	user_id,
	creator_id,
	dispatch,
	profile_context,
	property_id,
	property_created_by,
	screenType,
}: {
	url: string;
	loading: boolean;
	showOnPhone?: boolean;
	user_id: string | ObjectId;
	creator_id: string | ObjectId;
	dispatch: Function;
	profile_context: any;
	property_id: string | ObjectId;
	property_created_by: any;
	screenType: string;
}) => {
	const theme = useTheme();
	const router = React.useRef(useRouter()).current;

	const localLang = {
		heading:
			router.locale === "ar-AE"
				? "شارك هذا العقار"
				: router.locale === "kn-IN"
					? "ಈ ಸೇವೆಯನ್ನು ಶೇರ್ ಮಾಡಿ"
					: "Share this property",
		snackValue:
			router.locale === "ar-AE" ? "تم نسخ الرابط!" : router.locale === "kn-IN" ? "ಲಿಂಕ್ ಕಾಪಿ ಆಗಿದೆ!" : "Link copied!",
		whatsApp: router.locale === "ar-AE" ? "واتساب" : router.locale === "kn-IN" ? "ವಾಟ್ಸಾಪ್" : "WhatsApp",
		email: router.locale === "ar-AE" ? "البريد الإلكتروني" : router.locale === "kn-IN" ? "ಇ-ಮೇಲ್" : "Email",
		copy: router.locale === "ar-AE" ? "نسخ" : router.locale === "kn-IN" ? "ಕಾಪಿ" : "Copy",
		copyLink:
			router.locale === "ar-AE"
				? "أو انسخ هذا الرابط"
				: router.locale === "kn-IN"
					? "ಲಿಂಕ್ ಕಾಪಿ ಮಾಡಿ"
					: "Copy this link",
	};

	const [openSnackbar, setOpenSnackbar] = React.useState(false);

	if (loading) {
		return (
			<Container
				sx={{
					[theme.breakpoints.up("md1190")]: {
						display: showOnPhone ? "none" : "block",
					},
					[theme.breakpoints.down("md1190")]: {
						display: showOnPhone ? "block" : "none",
					},
				}}
			>
				<Heading>
					<Skeleton
						animation="wave"
						variant="rectangular"
						width="5rem"
						height="1rem"
						style={{ borderRadius: "0.5rem" }}
					/>
				</Heading>
				<SocialButtonsConatiner>
					<SocialPaper>
						<Skeleton
							animation="wave"
							variant="rectangular"
							width="2.5rem"
							height="2.5rem"
							style={{ borderRadius: "0.5rem" }}
						/>
					</SocialPaper>
					<SocialPaper>
						<Skeleton
							animation="wave"
							variant="rectangular"
							width="2.5rem"
							height="2.5rem"
							style={{ borderRadius: "0.5rem" }}
						/>
					</SocialPaper>
					<SocialPaper>
						<Skeleton
							animation="wave"
							variant="rectangular"
							width="2.5rem"
							height="2.5rem"
							style={{ borderRadius: "0.5rem" }}
						/>
					</SocialPaper>
				</SocialButtonsConatiner>
				<CopyRow>
					<Skeleton
						animation="wave"
						variant="rectangular"
						width="100%"
						height="3.5rem"
						style={{ borderRadius: "0.5rem" }}
					/>
				</CopyRow>
			</Container>
		);
	}

	const handleclickCopy = (url: string) => {
		navigator.clipboard.writeText(url);
		setOpenSnackbar(true);
	};

	const handleCloseSnackbar = (event: any, reason: any) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenSnackbar(false);
	};

	const handleShareClick = async () => {
		if (user_id === creator_id) {
			await dispatch(
				AddingBeegruPointsForSharePropertyThunk({
					user_id: user_id,
					url: url,
				}),
			);
		}
	};

	const handleShare = async (url: string, source: string) => {
		dispatch(
			CreateNewSharesThunk({
				entity_type: "property",
				entity_id: property_id,
				shared_through: source,
				entity_created_by: {
					is_business_profile: property_created_by.is_business_profile,
					user_id: property_created_by.user_id._id,
					business_profile_id: property_created_by.business_profile_id._id,
				},
				is_guest: user_id ? false : true,
				logged_user: user_id
					? {
							user_id: profile_context ? profile_context.user_id : "",
							business_profile_id: profile_context ? profile_context.business_profile_id : "",
							is_business_profile: profile_context ? profile_context.is_business_profile : false,
						}
					: null,
			}),
		);
	};

	return (
		<React.Fragment>
			<Snack
				open={openSnackbar}
				autoHideDuration={3000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={(e) => {
						handleCloseSnackbar(e, "");
					}}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{localLang.snackValue}
				</Alert>
			</Snack>

			<Container
				sx={{
					[theme.breakpoints.up("md1190")]: {
						display: showOnPhone ? "none" : "block",
					},
					[theme.breakpoints.down("md1190")]: {
						display: showOnPhone ? "block" : "none",
					},
				}}
			>
				<Heading>{localLang.heading}</Heading>

				<SocialButtonsConatiner>
					{/* Whatsapp */}

					<Link
						id={screenType + "_spp_whatsapp_share_button"}
						href={"https://wa.me/?text=Have a look at this property on Beegru " + url + "?source=whatsapp"}
						rel="noopener"
						target="_self"
						referrerPolicy="no-referrer"
						aria-label="Share via Whatsapp"
						sx={{ textDecoration: "none", color: "inherit" }}
						onClick={() => {
							user_id ? handleShareClick() : null;
							handleShare(url, "whatsapp");
						}}
					>
						<SocialPaper>
							<SvgIcon
								component={WhatsAppIcon}
								inheritViewBox={false}
								viewBox="0 0 24 24"
								width={24}
								height={24}
								sx={{
									width: "2rem",
									height: "2rem",
								}}
							/>
							<Label variant="body2">{localLang.whatsApp}</Label>
						</SocialPaper>
					</Link>
					{/* Divider */}
					<Divider
						orientation="horizontal"
						flexItem
						sx={{
							width: "1px",
							margin: "1rem 0",
							backgroundColor: theme.palette.divider,
						}}
					/>
					{/* Email */}

					<Link
						id={screenType + "_spp_email_share_button"}
						href={
							"mailto:?&subject=Have%20a%20look%20at%20this%20property%20on%20Beegru.&body=" + url + "?source=email"
						}
						rel="noopener"
						target="_self"
						referrerPolicy="no-referrer"
						aria-label="Share via Email"
						sx={{ textDecoration: "none", color: "inherit" }}
						onClick={() => {
							user_id ? handleShareClick() : null;
							handleShare(url, "email");
						}}
					>
						<SocialPaper>
							<SvgIcon
								component={MailIcon}
								inheritViewBox={false}
								viewBox="0 0 24 24"
								width={24}
								height={24}
								sx={{
									width: "2rem",
									height: "2rem",
								}}
							/>
							<Label variant="body2">{localLang.email}</Label>
						</SocialPaper>
					</Link>

					<Divider
						orientation="horizontal"
						flexItem
						sx={{
							width: "1px",
							margin: "1rem 0",
							backgroundColor: theme.palette.divider,
						}}
					/>

					{/* Copy */}

					<SocialPaper>
						<IconButton
							id={screenType + "_spp_copy_share_button"}
							onClick={(e: any) => {
								handleclickCopy(url + "?source=copy");
								handleShare(url, "copy");
							}}
							sx={{
								height: "2.5rem",
								width: "2.5rem",
								borderRadius: "0.5rem",
								// backgroundColor: "var(--Primary-New-States-Outlined-Hover-Background, rgba(252, 128, 25, 0.16))",
								color: "#B05911",
								padding: "0.5rem",
							}}
							size="medium"
						>
							<SvgIcon
								component={CopyIcon}
								inheritViewBox={false}
								viewBox="0 0 25 25"
								width={24}
								height={24}
								sx={{
									width: "2rem",
									height: "2rem",
								}}
							/>
							{/* <ContentCopyOutlinedIcon sx={{ height: "100%", width: "100%" }} /> */}
						</IconButton>
						{/* <ImgTag
							src={
								theme.palette.mode === "dark"
									? "/images/icons/social/copy-white.svg"
									: "/images/icons/social/copy-orange.svg"
							}
							alt="Copy"
							loading="lazy"
							referrerPolicy="no-referrer"
							onClick={(e: any) => {
								handleclickCopy(url + "?source=copy");
							}}
						/> */}
						<Label variant="body2">{localLang.copy}</Label>
					</SocialPaper>
				</SocialButtonsConatiner>
			</Container>
		</React.Fragment>
	);
};

export default ShareProperty;
